import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header/Header';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { gatewaySet } from './config';
import {
  SkeletonPlaceholder,
  ButtonSkeleton,
  Button,
  Modal,
  DataTableSkeleton,
} from '@carbon/react';
import {
  Error500Type,
  NetworkSegmentData,
  NetworkSegment,
} from '../../models/master';
import { getResourceGroups } from '../../controllers/resourceGroupApi';
import {
  getNetworkSegmentDetails,
  deleteNetworkSegment,
  getNetworkSegments,
} from '../../controllers/networksegmentsApi';
import DetailsCard from '../../components/DetailsCard/DetailsCard';
import { AxiosError } from 'axios';
import EditNetworkSegment from './EditNetworkSegment/EditNetworkSegment';
import { ResourceGroup } from '../../models/master';
import Error500 from '../Errors/Error500';
import images from '../../images/images';
import useAnalytics from '../../lib/useAnalytics';
import NetworkSegmentResourcesList from './NetworkSegmentResources/NetworkSegmentResourcesList';
import { NetworkSegmentCompatibilitySet } from '../../lib/enums';
import { DEFAULT_NETWORK_SEGMENT_ID } from '../../lib/constants';
import Error403Card from '../../components/ErrorState/Error403Card';
import ProceduralActivity from '../../components/ProceduralActivity/ProceduralActivity';
import OffboardNetworkSegment from './OffboardNetworkSegment/OffboardNetworkSegment';

import './NetworkSegmentDetails.scss';

type NetworkSegmentState = {
  breadcrumb?: any;
};

const defaultPermissionMap = {
  resourceGroups: true,
  networkSegmentDetails: true,
  networkSegmentList: true,
};

const NetworkSegmentDetails = () => {
  const { t } = useTranslation('networkSegmentDetails');
  const [networkSegmentData, setNetworkSegmentData] =
    useState<NetworkSegment | null>(null);
  const [networkSegmentDataLoading, toggleNetworkSegmentDataLoading] =
    useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const networkSegmentId = searchParams.get('nwSegId');
  const [showEditNetworkSegment, setShowEditNetworkSegment] =
    useState<boolean>(false);
  const [error500, setError500] = useState<null | Error500Type>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as NetworkSegmentState;

  const [resourceGroupData, setResourceGroupsData] = useState<ResourceGroup[]>(
    []
  );
  const [networkSegments, setNetworkSegments] = useState<
    NetworkSegment[] | null
  >(null);
  const [permissionMap, setPermissionMap] = useState(defaultPermissionMap);
  const [offboardModalOpen, toggleOffboardModal] = useState(false);

  const handleNetworkSegmentUpdate = (networkSegmentData: NetworkSegment) => {
    setNetworkSegmentData(networkSegmentData);

    setNetworkSegments(prevData =>
      Array.isArray(prevData)
        ? prevData.map(networkSegment => {
            if (networkSegment.resource_id === networkSegmentData.resource_id) {
              return networkSegmentData;
            }

            return networkSegment;
          })
        : prevData
    );
  };

  const { pageViewed } = useAnalytics();

  const fetchResourceGroups = async () => {
    try {
      const response = await getResourceGroups(true);
      const resourceData = response.resource_groups as ResourceGroup[];
      setResourceGroupsData(resourceData ?? []);
    } catch (error) {
      console.error(error);
      const axiosErr = error as AxiosError;
      if (axiosErr?.response?.status === 403) {
        setPermissionMap(permissionMap => ({
          ...permissionMap,
          resourceGroups: false,
        }));
      }
    }
  };

  const fetchNwSegmentsList = async () => {
    try {
      const resp = await getNetworkSegments();
      setNetworkSegments(resp);
    } catch (error) {
      console.error(error);
      const axiosErr = error as AxiosError;
      if (axiosErr?.response?.status === 403) {
        setPermissionMap(permissionMap => ({
          ...permissionMap,
          networkSegmentList: false,
        }));
      }
    }
  };

  useEffect(() => {
    pageViewed('Network Segment Details');
    toggleNetworkSegmentDataLoading(true);
    refreshPage();
  }, []);

  useEffect(() => {
    networkSegmentData !== null
      ? toggleNetworkSegmentDataLoading(false)
      : toggleNetworkSegmentDataLoading(true);
  }, [networkSegmentData]);

  useEffect(() => {
    let interval: any;

    if (
      networkSegmentData?.procedural_activity === 'connecting-gateways' ||
      networkSegmentData?.procedural_activity === 'finalizing-onboarding'
    ) {
      interval = setInterval(() => {
        refreshNetworkSegmentDetails();
      }, 10000);
    } else {
      clearTimeout(interval);
    }

    return () => clearInterval(interval);
  }, [networkSegmentData]);

  const refreshNetworkSegmentDetails = async () => {
    try {
      const networkSegmentDetails = await getNetworkSegmentDetails(
        networkSegmentId
      );

      setNetworkSegmentData(networkSegmentDetails);
    } catch (error) {
      console.log(error);
    }
  };

  const refreshPage = async () => {
    try {
      setPermissionMap(defaultPermissionMap);
      setNetworkSegmentData(null);
      await fetchResourceGroups();
      await fetchNwSegmentsList();
      await getNetworkSegmentDetails(networkSegmentId)
        .then(data => {
          setNetworkSegmentData(data as NetworkSegment);
        })
        .catch((error: any) => {
          if (error?.response?.status === 404) {
            navigate('/404');
          }

          if (error?.response?.status === 403) {
            setPermissionMap(permissionMap => ({
              ...permissionMap,
              networkSegmentDetails: false,
            }));
          }

          if (error.response?.status >= 500) {
            setError500(error.response?.status?.toString());
          }
          console.log(error);
        });
    } catch (error: any) {
      console.log(error);
    } finally {
      toggleNetworkSegmentDataLoading(false);
    }
  };

  const onClose = () => {
    setShowEditNetworkSegment(false);
  };

  const editBtnClick = () => {
    setShowEditNetworkSegment(true);
  };

  const getResourceGroupName = (id: string | undefined) => {
    const resourceGroup =
      id && Array.isArray(resourceGroupData)
        ? resourceGroupData!.find(
            resourceGroup => resourceGroup.resource_id === id
          )
        : null;

    return resourceGroup ? resourceGroup.name : '-';
  };

  const getFormattedNetworkDetails = () => {
    let detailsData: {
      key: string;
      value: string | string[] | JSX.Element | undefined;
    }[] = [
      {
        key: 'name',
        value: networkSegmentData?.name ? networkSegmentData?.name : '—',
      },
      {
        key: 'gatewaySet',
        value: gatewaySet.find(
          (gateway: any) =>
            networkSegmentData?.compatibility_set === gateway.value
        )?.name,
      },
      {
        key: 'infrastructureGroup',
        value: getResourceGroupName(networkSegmentData?.['resource_group_id']),
      },
      {
        key: 'created',
        value: networkSegmentData?.created_at
          ? networkSegmentData?.created_at
          : '—',
      },
      {
        key: 'labels',
        value: networkSegmentData?.labels ? networkSegmentData?.labels : '—',
      },
      {
        key: 'description',
        value: networkSegmentData?.description
          ? networkSegmentData?.description
          : '—',
      },
    ];
    if (networkSegmentData?.compatibility_set === 'RHSI') {
      if (networkSegmentData.unmanaged) {
        detailsData.splice(2, 0, {
          key: 'onboardingStatus',
          value: (
            <ProceduralActivity
              status={networkSegmentData.procedural_activity}
            />
          ),
        });

        detailsData.splice(3, 0, {
          key: 'metricCollection',
          value: networkSegmentData?.flow_collector
            ? networkSegmentData?.flow_collector?.toString()
            : 'false',
        });

        // // Uncomment this when sync_status key is available
        // detailsData.splice(5, 0, {
        //   key: 'syncStatus',
        //   value: networkSegmentData?.sync_status
        //     ? networkSegmentData?.sync_status === true
        //       ? t('on')
        //       : t('off')
        //     : t('off'),
        // });
      } else {
        detailsData.splice(2, 0, {
          key: 'metricCollection',
          value: networkSegmentData?.flow_collector
            ? networkSegmentData?.flow_collector?.toString()
            : 'false',
        });

        // // Uncomment this when sync_status key is available
        // detailsData.splice(4, 0, {
        //   key: 'syncStatus',
        //   value: networkSegmentData?.sync_status
        //     ? networkSegmentData?.sync_status === true
        //       ? t('on')
        //       : t('off')
        //     : t('off'),
        // });
      }
    }

    return detailsData;
  };

  const getInfraGroups = () => {
    return resourceGroupData?.filter(
      resource => resource.type === 'infrastructure'
    );
  };

  if (error500) {
    return <Error500 />;
  }

  const getSubTitle = () => {
    return (
      <div className='sub-heading-section'>
        <div
          className={
            'header-icon-contaniner ' +
            (networkSegmentData?.unmanaged ? 'unmanaged' : 'managed')
          }
        >
          {networkSegmentData?.unmanaged
            ? images.unmanagedIcon()
            : images.managedIcon()}
          <div className='icon-text'>
            {networkSegmentData?.unmanaged ? t('unmanaged') : t('managed')}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className='network-segment-details resource-details'
      id='networkSegmentDetails'
      data-testid='network-segment-details-container'
    >
      <Header
        loading={networkSegmentDataLoading}
        title={networkSegmentData ? networkSegmentData.name : ''}
        subTitle={getSubTitle()}
        breadcrumbs={
          state !== null && state.breadcrumb
            ? state.breadcrumb
            : [
                {
                  url: '/',
                  label: t('home'),
                },
                {
                  url: '/networkSegments',
                  label: t('networkSegments'),
                },
              ]
        }
        dataTestId='network-segment-header'
      />
      <div className='network-segment-details-container'>
        {networkSegmentDataLoading ? (
          <SkeletonPlaceholder className='details-tile-skeleton' />
        ) : !permissionMap['networkSegmentDetails'] ? (
          <Error403Card />
        ) : (
          <>
            <DetailsCard
              type='NETWORK_SEGMENT_DETAILS'
              openEditModal={editBtnClick}
              isEditable={
                networkSegmentData?.resource_id !== DEFAULT_NETWORK_SEGMENT_ID
                  ? true
                  : false
              }
              detailsCardName={t('detailsCardName')}
              data={getFormattedNetworkDetails()}
              dataTestId='network-segment-details-card'
            />
          </>
        )}
      </div>
      {networkSegmentId !== DEFAULT_NETWORK_SEGMENT_ID &&
      networkSegmentDataLoading ? (
        <DataTableSkeleton />
      ) : !permissionMap['networkSegmentDetails'] ? null : (
        networkSegmentData?.compatibility_set ===
          NetworkSegmentCompatibilitySet.ServiceInterconnect && (
          <div
            className='network-segment-resources-list-container'
            data-testid='ns-resources-list-container'
          >
            <NetworkSegmentResourcesList
              networkSegmentData={networkSegmentData}
              resourceGroups={resourceGroupData}
              resourceGroupPermission={permissionMap.resourceGroups}
              refreshNetworkSegmentDetails={refreshNetworkSegmentDetails}
            />
          </div>
        )
      )}

      {networkSegmentData?.onboarding_type === 'brownfield' ? (
        <div
          className='offboard-network-segment-button'
          data-testid='network-segment-offboard-button'
        >
          {
            <Button
              kind='danger--ghost'
              onClick={() => {
                toggleOffboardModal(true);
              }}
            >
              {t('offboardNetworkSegment')}
            </Button>
          }
        </div>
      ) : (
        <div className='offboard-network-segment-button'></div>
      )}

      {offboardModalOpen && (
        <OffboardNetworkSegment
          networkSegment={networkSegmentData}
          open={offboardModalOpen}
          closeTearsheet={() => toggleOffboardModal(false)}
        />
      )}

      <EditNetworkSegment
        open={showEditNetworkSegment}
        onClose={() => onClose()}
        onUpdate={handleNetworkSegmentUpdate}
        networkSegmentData={networkSegmentData}
        networkSegments={networkSegments}
        infraResourceGroups={getInfraGroups()}
      />
    </div>
  );
};

export default NetworkSegmentDetails;
